/**
 * Cascading menus over the navigation tree — WAI-ARIA `menubar` / `menu`
 * patterns, any depth. Shared by every layout that drops menus (the macOS
 * menu bar, the mega-menu's overflow, the rail's fly-outs, Office's Menu).
 *
 * Keyboard: in a menu, Up/Down move, Home/End jump, Right (or Enter/Space on a
 * folder) opens its submenu and focuses the first entry, Left / Esc closes the
 * submenu and returns to its parent entry, Esc on the top menu closes it and
 * returns focus to the button that opened it. In a menubar, Left/Right move
 * between the top entries and Down opens the focused one.
 */
import { ChevronRight, Star } from 'lucide-react'
import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
  type CSSProperties, type KeyboardEvent, type ReactNode,
} from 'react'
import { tw } from '../../../testHooks'
import { NavIcon } from '../navIcons'
import { S } from '../tokens'
import type { NavTreeNode } from '../tree'
import type { ShellNavModel } from '../useShellNav'
import { useEntryMenu } from './ShellLists'

const menuStyle: CSSProperties = {
  minWidth: 240, maxWidth: 360, maxHeight: '70vh', overflowY: 'auto', padding: 4,
  borderRadius: 10, background: S.raised, color: S.text,
  border: `1px solid ${S.border}`, boxShadow: '0 14px 34px rgba(0,0,0,0.24)',
}

function focusables(el: HTMLElement | null): HTMLElement[] {
  if (!el) return []
  return [...el.querySelectorAll<HTMLElement>(':scope > [role="none"] > [role="menuitem"]')]
}

export interface CascadeMenuProps {
  model: ShellNavModel
  nodes: NavTreeNode[]
  label: string
  /** A screen was opened — close everything. */
  onPicked: () => void
  /** Esc / Left on the top menu: close it (the owner refocuses its button). */
  onCloseTop?: () => void
  /** Called with Left/Right on the TOP menu so a menubar can move on. */
  onSideKey?: (dir: -1 | 1) => void
  autoFocus?: boolean
  style?: CSSProperties
  /** Content before the entries (a heading, Pinned…). */
  header?: ReactNode
}

export function CascadeMenu({
  model, nodes, label, onPicked, onCloseTop, onSideKey, autoFocus = true, style, header,
}: CascadeMenuProps) {
  const entryMenu = useEntryMenu(model)
  return (
    <>
      <MenuLevel model={model} nodes={nodes} label={label} onPicked={onPicked} onClose={onCloseTop}
        onSideKey={onSideKey} autoFocus={autoFocus} style={style} header={header} entryMenu={entryMenu} top />
      {entryMenu.ui}
    </>
  )
}

function MenuLevel({
  model, nodes, label, onPicked, onClose, onSideKey, autoFocus, style, header, entryMenu, top = false,
}: CascadeMenuProps & { onClose?: () => void; entryMenu: ReturnType<typeof useEntryMenu>; top?: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [focusChild, setFocusChild] = useState(false)
  useEffect(() => {
    if (autoFocus) focusables(ref.current)[0]?.focus()
  }, [autoFocus])

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement && !e.currentTarget.contains(e.target)) return
    const els = focusables(ref.current)
    const i = els.indexOf(document.activeElement as HTMLElement)
    if (i < 0) return // the key belongs to a submenu below
    const go = (j: number) => { e.preventDefault(); e.stopPropagation(); els[(j + els.length) % els.length]?.focus() }
    const cur = els[i]
    const id = cur?.dataset.nodeId || ''
    const node = nodes.find((n) => n.item.id === id)
    switch (e.key) {
      case 'ArrowDown': go(i + 1); break
      case 'ArrowUp': go(i - 1); break
      case 'Home': go(0); break
      case 'End': go(els.length - 1); break
      case 'ArrowRight':
        e.preventDefault(); e.stopPropagation()
        if (node?.isFolder && node.children.length) { setOpenId(id); setFocusChild(true) } else onSideKey?.(1)
        break
      case 'ArrowLeft':
        e.preventDefault(); e.stopPropagation()
        if (top) onSideKey?.(-1)
        else onClose?.()
        break
      case 'Escape':
        e.preventDefault(); e.stopPropagation()
        onClose?.()
        break
      case 'Tab':
        onClose?.()
        break
      default:
    }
  }

  return (
    <div ref={ref} role="menu" aria-label={label} onKeyDown={onKeyDown} style={{ ...menuStyle, ...style }}>
      {header}
      {nodes.length === 0 && (
        <div role="none" style={{ padding: '8px 12px', fontSize: 14, color: S.text2 }}>No screens yet</div>
      )}
      {nodes.map((n) => (
        <MenuEntry key={n.item.id} node={n} model={model} open={openId === n.item.id}
          focusChild={openId === n.item.id && focusChild}
          onOpenSub={(on, viaKey) => { setOpenId(on ? n.item.id : null); setFocusChild(!!viaKey) }}
          onPicked={onPicked} entryMenu={entryMenu} />
      ))}
    </div>
  )
}

function MenuEntry({ node, model, open, focusChild, onOpenSub, onPicked, entryMenu }: {
  node: NavTreeNode
  model: ShellNavModel
  open: boolean
  focusChild: boolean
  onOpenSub: (on: boolean, viaKey?: boolean) => void
  onPicked: () => void
  entryMenu: ReturnType<typeof useEntryMenu>
}) {
  const { item, isFolder, children } = node
  const btnRef = useRef<HTMLButtonElement>(null)
  const subRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  const hasSub = isFolder && children.length > 0
  const current = model.current?.id === item.id
  const pinned = !isFolder && model.isPinned(item)

  useLayoutEffect(() => {
    if (!open || !btnRef.current) { setPos(null); return }
    const r = btnRef.current.getBoundingClientRect()
    const w = 280
    const left = r.right + w > window.innerWidth ? Math.max(8, r.left - w) : r.right - 2
    setPos({ left, top: Math.max(8, Math.min(r.top - 4, window.innerHeight - 120)) })
  }, [open])

  const closeSub = useCallback(() => { onOpenSub(false); btnRef.current?.focus() }, [onOpenSub])

  return (
    <div role="none"
      onMouseEnter={() => { if (hasSub) onOpenSub(true) }}
      onMouseLeave={(e) => {
        if (hasSub && !subRef.current?.contains(e.relatedTarget as Node)) onOpenSub(false)
      }}>
      <button
        ref={btnRef}
        type="button"
        role="menuitem"
        data-node-id={item.id}
        aria-haspopup={hasSub ? 'menu' : undefined}
        aria-expanded={hasSub ? open : undefined}
        aria-current={current ? 'page' : undefined}
        className="tw-sh-item"
        {...tw('nav', item.obj_name || item.title, { type: item.obj_type, folder: isFolder, selected: current })}
        onClick={() => {
          if (hasSub) { onOpenSub(!open, true); return }
          if (isFolder) return
          model.open(item)
          onPicked()
        }}
        onKeyDown={(e) => {
          if (hasSub && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpenSub(true, true) }
          entryMenu.onKeyDown(item)(e)
        }}
        onContextMenu={entryMenu.onContextMenu(item)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 40, padding: '0 10px',
          border: 'none', borderRadius: 7, cursor: isFolder && !hasSub ? 'default' : 'pointer',
          background: open || current ? S.surface2 : 'transparent', color: S.text,
          fontFamily: 'inherit', fontSize: 14.5, fontWeight: current ? 600 : 400, textAlign: 'left',
        }}
      >
        <NavIcon node={item} size={16} color={current ? S.accent : S.text2} />
        <span className="tw-sh-clip" style={{ flex: 1, minWidth: 0 }}>{item.title}</span>
        {pinned && <Star size={13} aria-label="Pinned" role="img" fill={S.star} color={S.starStroke} />}
        {hasSub && <ChevronRight size={15} aria-hidden color={S.faint} />}
      </button>
      {hasSub && open && pos && (
        <div ref={subRef} style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 9442 }}
          onMouseLeave={(e) => { if (!btnRef.current?.contains(e.relatedTarget as Node)) onOpenSub(false) }}>
          <MenuLevel model={model} nodes={children} label={item.title} onPicked={onPicked}
            onClose={closeSub} autoFocus={focusChild} entryMenu={entryMenu} style={{ width: 280 }} />
        </div>
      )}
    </div>
  )
}

/**
 * A horizontal menubar of top-level entries, each dropping a CascadeMenu.
 * A top-level SCREEN (not a folder) is a plain menubar item that opens it.
 */
export function NavMenuBar({ model, nodes, label = 'Menu bar', itemStyle, renderItemLabel }: {
  model: ShellNavModel
  nodes: NavTreeNode[]
  label?: string
  itemStyle?: (open: boolean, node: NavTreeNode) => CSSProperties
  renderItemLabel?: (node: NavTreeNode) => ReactNode
}) {
  const barRef = useRef<HTMLDivElement>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  const btns = () => [...(barRef.current?.querySelectorAll<HTMLElement>(':scope > [role="menuitem"]') || [])]
  const openAt = (id: string | null) => {
    setOpenId(id)
    if (!id) { setPos(null); return }
    const el = btns().find((b) => b.dataset.nodeId === id)
    if (el) { const r = el.getBoundingClientRect(); setPos({ left: r.left, top: r.bottom + 4 }) }
  }
  const move = (dir: -1 | 1, from?: string | null) => {
    const els = btns()
    const i = els.findIndex((b) => b.dataset.nodeId === (from ?? openId)) >= 0
      ? els.findIndex((b) => b.dataset.nodeId === (from ?? openId))
      : els.indexOf(document.activeElement as HTMLElement)
    const next = els[(i + dir + els.length) % els.length]
    next?.focus()
    if (openId && next?.dataset.hasSub === '1') openAt(next.dataset.nodeId || null)
    else if (openId) openAt(null)
  }
  useEffect(() => {
    if (!openId) return undefined
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (barRef.current?.contains(t)) return
      if ((t as HTMLElement).closest?.('[data-tw-shell-menu]')) return
      openAt(null)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId])

  const openNode = openId ? nodes.find((n) => n.item.id === openId) : undefined
  return (
    <div ref={barRef} role="menubar" aria-label={label} style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
      {nodes.map((n, idx) => {
        const hasSub = n.isFolder
        const open = openId === n.item.id
        return (
          <button
            key={n.item.id}
            type="button"
            role="menuitem"
            data-node-id={n.item.id}
            data-has-sub={hasSub ? '1' : '0'}
            tabIndex={idx === 0 ? 0 : -1}
            aria-haspopup={hasSub ? 'menu' : undefined}
            aria-expanded={hasSub ? open : undefined}
            className="tw-sh-btn"
            {...tw('nav', n.item.obj_name || n.item.title, { type: n.item.obj_type, folder: n.isFolder })}
            onClick={() => {
              if (hasSub) { openAt(open ? null : n.item.id); return }
              model.open(n.item)
            }}
            onMouseEnter={() => { if (openId && hasSub && !open) openAt(n.item.id) }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') { e.preventDefault(); move(1, n.item.id) }
              if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1, n.item.id) }
              if ((e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') && hasSub) {
                e.preventDefault(); openAt(n.item.id)
              }
              if (e.key === 'Escape' && open) { e.preventDefault(); openAt(null) }
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 10px',
              border: 'none', borderRadius: 6, background: open ? S.surface2 : 'transparent',
              color: S.text, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, whiteSpace: 'nowrap',
              ...(itemStyle ? itemStyle(open, n) : {}),
            }}
          >
            {renderItemLabel ? renderItemLabel(n) : (<><NavIcon node={n.item} size={15} />{n.item.title}</>)}
          </button>
        )
      })}
      {openNode && pos && (
        <div data-tw-shell-menu="" style={{ position: 'fixed', left: Math.min(pos.left, window.innerWidth - 300), top: pos.top, zIndex: 9441 }}>
          <CascadeMenu model={model} nodes={openNode.children} label={openNode.item.title}
            onPicked={() => openAt(null)}
            onCloseTop={() => {
              const back = btns().find((b) => b.dataset.nodeId === openNode.item.id)
              openAt(null); back?.focus()
            }}
            onSideKey={(dir) => move(dir, openNode.item.id)} />
        </div>
      )}
    </div>
  )
}
